import isEqual from 'lodash/isEqual'
import { Query } from '@skedulo/protobuf-web'
import { IComparatorOption, IFilterOption } from '../types'

export const COMPARATOR_OPTIONS: Record<string, IComparatorOption> = {
  equals: {
    value: 'EQUALS',
    label: 'is',
    enums: [Query.API.LuceneQuery_Condition_Comparison.EQUAL],
    kind: [
      // Everything except datetime and date
      Query.API.DataField_Kind.BOOL,
      Query.API.DataField_Kind.DATA_FIELD_KIND_UNSPECIFIED,
      Query.API.DataField_Kind.ENUM,
      Query.API.DataField_Kind.OBJECT,
      Query.API.DataField_Kind.RELATED_OBJECT,
      Query.API.DataField_Kind.STRING,
    ],
    not: false,
  },
  equalsNumber: {
    value: 'EQUALS',
    label: 'equals',
    enums: [Query.API.LuceneQuery_Condition_Comparison.EQUAL],
    kind: [
      Query.API.DataField_Kind.DECIMAL,
      Query.API.DataField_Kind.INT,
      Query.API.DataField_Kind.TIME,
    ],
    not: false,
  },
  notEqualsNumber: {
    value: 'NOT_EQUALS',
    label: 'not equal to',
    enums: [Query.API.LuceneQuery_Condition_Comparison.EQUAL],
    kind: [
      Query.API.DataField_Kind.DECIMAL,
      Query.API.DataField_Kind.INT,
      Query.API.DataField_Kind.TIME,
    ],
    not: false,
  },
  isDateTime: {
    value: 'BETWEEN',
    label: 'is',
    enums: [
      Query.API.LuceneQuery_Condition_Comparison.MORE_THAN,
      Query.API.LuceneQuery_Condition_Comparison.LESS_THAN,
    ],
    kind: [Query.API.DataField_Kind.DATETIME, Query.API.DataField_Kind.DATE],
    not: false,
  },
  doesNotEqual: {
    value: 'NOT_EQUALS',
    label: 'is not',
    not: true,
    enums: [Query.API.LuceneQuery_Condition_Comparison.EQUAL],
    kind: [
      Query.API.DataField_Kind.BOOL,
      Query.API.DataField_Kind.DATA_FIELD_KIND_UNSPECIFIED,
      Query.API.DataField_Kind.ENUM,
      Query.API.DataField_Kind.OBJECT,
      Query.API.DataField_Kind.RELATED_OBJECT,
      Query.API.DataField_Kind.STRING,
      Query.API.DataField_Kind.DATETIME,
      Query.API.DataField_Kind.DATE,
    ],
  },
  greaterThan: {
    value: 'GREATER',
    label: 'greater than',
    enums: [Query.API.LuceneQuery_Condition_Comparison.MORE_THAN],
    kind: [
      Query.API.DataField_Kind.INT,
      Query.API.DataField_Kind.DECIMAL,
      Query.API.DataField_Kind.TIMESTAMP,
    ],
    not: false,
  },
  greaterOrEqual: {
    value: 'GREATER_EQUAL',
    label: 'greater or equal to',
    enums: [Query.API.LuceneQuery_Condition_Comparison.MORE_THAN_EQUAL],
    kind: [
      Query.API.DataField_Kind.INT,
      Query.API.DataField_Kind.DECIMAL,
      Query.API.DataField_Kind.TIMESTAMP,
    ],
    not: false,
  },
  lessThan: {
    value: 'LESS',
    label: 'less than',
    enums: [Query.API.LuceneQuery_Condition_Comparison.LESS_THAN],
    kind: [
      Query.API.DataField_Kind.INT,
      Query.API.DataField_Kind.DECIMAL,
      Query.API.DataField_Kind.TIMESTAMP,
    ],
    not: false,
  },
  lessOrEqual: {
    value: 'LESS_EQUAL',
    label: 'less or equal to',
    enums: [Query.API.LuceneQuery_Condition_Comparison.LESS_THAN_EQUAL],
    kind: [
      Query.API.DataField_Kind.INT,
      Query.API.DataField_Kind.DECIMAL,
      Query.API.DataField_Kind.TIMESTAMP,
    ],
    not: false,
  },
  dateGreaterThan: {
    value: 'GREATER',
    label: 'is after',
    enums: [Query.API.LuceneQuery_Condition_Comparison.MORE_THAN],
    kind: [Query.API.DataField_Kind.DATE, Query.API.DataField_Kind.DATETIME],
    not: false,
  },
  dateGreaterOrEqual: {
    value: 'GREATER_EQUAL',
    label: 'is on or after',
    enums: [Query.API.LuceneQuery_Condition_Comparison.MORE_THAN_EQUAL],
    kind: [Query.API.DataField_Kind.DATE, Query.API.DataField_Kind.DATETIME],
    not: false,
  },
  dateLessThan: {
    value: 'LESS',
    label: 'is before',
    enums: [Query.API.LuceneQuery_Condition_Comparison.LESS_THAN],
    kind: [Query.API.DataField_Kind.DATE, Query.API.DataField_Kind.DATETIME],
    not: false,
  },
  dateLessOrEqual: {
    value: 'LESS_EQUAL',
    label: 'is on or before',
    enums: [Query.API.LuceneQuery_Condition_Comparison.LESS_THAN_EQUAL],
    kind: [Query.API.DataField_Kind.DATE, Query.API.DataField_Kind.DATETIME],
    not: false,
  },
  contains: {
    value: 'CONTAINS',
    label: 'contains',
    enums: [Query.API.LuceneQuery_Condition_Comparison.CONTAINS],
    kind: [Query.API.DataField_Kind.STRING],
    not: false,
    allowForRelated: false,
  },
  doesNotContain: {
    value: 'NOT_CONTAINS',
    label: 'does not contain',
    enums: [Query.API.LuceneQuery_Condition_Comparison.CONTAINS],
    kind: [Query.API.DataField_Kind.STRING],
    not: true,
    allowForRelated: false,
  },
  startsWith: {
    value: 'STARTS_WITH',
    label: 'starts with',
    enums: [Query.API.LuceneQuery_Condition_Comparison.STARTS_WITH],
    not: false,
    allowForRelated: false,
  },
  doesNotStartWith: {
    value: 'NOT_STARTS_WITH',
    label: 'does not start with',
    enums: [Query.API.LuceneQuery_Condition_Comparison.STARTS_WITH],
    not: true,
    allowForRelated: false,
  },
  in: {
    value: 'IN',
    label: 'is one of',
    enums: [Query.API.LuceneQuery_Condition_Comparison.IN],
    not: false,
  },
  notIn: {
    value: 'NOT_IN',
    label: 'is not one of',
    enums: [Query.API.LuceneQuery_Condition_Comparison.IN],
    not: true,
  },
  // No `enums` entry: these have no backend Comparison equivalent, so they are never
  // selected by getComparatorOptionsFromAcceptableFilters and must be appended separately
  // for nullable fields - see appendNullComparatorOptions.
  isNull: {
    value: 'IS_NULL',
    label: 'is null',
    not: false,
  },
  isNotNull: {
    value: 'IS_NOT_NULL',
    label: 'is not null',
    not: true,
  },
}

/**
 * Appends the "is null" / "is not null" comparators for a nullable field.
 * These have no backend Comparison enum equivalent (unlike the other comparators here),
 * so they can't flow through the acceptableFilters matching in
 * getComparatorOptionsFromAcceptableFilters - only the EQL query builder can build a real
 * query from them today, see getFieldComparatorOptions.
 *
 * Only appends onto a non-empty list: a field whose kind has no comparators at all
 * (e.g. an unhandled DataField_Kind) should stay unfilterable rather than gain
 * "is null"/"is not null" as its only options.
 */
export const appendNullComparatorOptions = (
  options: IComparatorOption[],
  field: Pick<IFilterOption, 'isNullable' | 'isList'>,
): IComparatorOption[] =>
  options.length > 0 && field.isNullable && !field.isList
    ? [...options, COMPARATOR_OPTIONS.isNull, COMPARATOR_OPTIONS.isNotNull]
    : options

// isNull/isNotNull have no `enums`, so they're excluded here - they have no backend
// Comparison equivalent for getComparatorOptionsFromAcceptableFilters to match against.
const searchableOptions = Object.keys(COMPARATOR_OPTIONS).reduce<
  IComparatorOption[]
>(
  (result, option) =>
    COMPARATOR_OPTIONS[option].enums
      ? [...result, COMPARATOR_OPTIONS[option]]
      : result,
  [],
)

/**
 * Filters the list of options in line with the acceptable filters array
 * - all enums on the options must be present in the acceptable filters array
 * - an enum of this option that matches the "not" must also be present
 * - the kind must match the supplied kind (this is to different date gt from number gt)
 */
export const getComparatorOptionsFromAcceptableFilters = (
  kind: Query.API.DataField_Kind,
  acceptableFilters: Query.API.Comparison[],
  relational?: boolean,
): IComparatorOption[] => {
  return searchableOptions
    .filter((option) => {
      const matchedComparators = acceptableFilters
        .filter(
          (filter) =>
            option.enums &&
            option.enums.find((opt) => opt === filter.comparison),
        )
        .filter(() => !option.kind || option.kind.includes(kind))
        .filter((filter) => option.not === filter.not)
        .map((option) => option.comparison)

      return isEqual(matchedComparators, option.enums)
    })
    .filter(
      (comp) =>
        comp.allowForRelated === undefined ||
        comp.allowForRelated === relational,
    )
}

export const getComparatorOptions = (
  type?: Query.API.DataField_Kind,
  isList?: boolean,
  relational?: boolean,
) => {
  if (isList)
    return [COMPARATOR_OPTIONS.equals, COMPARATOR_OPTIONS.doesNotEqual]

  let result: IComparatorOption[] = []

  switch (type) {
    case Query.API.DataField_Kind.ENUM:
      result = [
        COMPARATOR_OPTIONS.equals,
        COMPARATOR_OPTIONS.doesNotEqual,
        COMPARATOR_OPTIONS.in,
        COMPARATOR_OPTIONS.notIn,
      ]
      break
    case Query.API.DataField_Kind.STRING:
      result = [
        COMPARATOR_OPTIONS.equals,
        COMPARATOR_OPTIONS.doesNotEqual,
        COMPARATOR_OPTIONS.contains,
        COMPARATOR_OPTIONS.doesNotContain,
        COMPARATOR_OPTIONS.startsWith,
        COMPARATOR_OPTIONS.doesNotStartWith,
      ]
      break
    case Query.API.DataField_Kind.DATETIME:
    case Query.API.DataField_Kind.DATE:
      result = [
        COMPARATOR_OPTIONS.isDateTime,
        COMPARATOR_OPTIONS.doesNotEqual,
        COMPARATOR_OPTIONS.dateGreaterThan,
        COMPARATOR_OPTIONS.dateGreaterOrEqual,
        COMPARATOR_OPTIONS.dateLessThan,
        COMPARATOR_OPTIONS.dateLessOrEqual,
      ]
      break
    case Query.API.DataField_Kind.INT:
    case Query.API.DataField_Kind.DECIMAL:
    case Query.API.DataField_Kind.TIMESTAMP:
      result = [
        COMPARATOR_OPTIONS.equals,
        COMPARATOR_OPTIONS.doesNotEqual,
        COMPARATOR_OPTIONS.greaterThan,
        COMPARATOR_OPTIONS.greaterOrEqual,
        COMPARATOR_OPTIONS.lessThan,
        COMPARATOR_OPTIONS.lessOrEqual,
      ]
      break
    case Query.API.DataField_Kind.BOOL:
      result = [COMPARATOR_OPTIONS.equals, COMPARATOR_OPTIONS.doesNotEqual]
      break
    default:
      result = []
  }

  return result.filter(
    (comp) =>
      comp.allowForRelated === undefined || comp.allowForRelated === relational,
  )
}
